import React, { useState } from "react";
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  CssBaseline,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Divider
} from "@mui/material";
import {
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
  Microsoft as MicrosoftIcon
} from '@mui/icons-material';
import axios from "axios";
import { useAuth } from "./AuthContext";
import { buildApiUrl } from "./config";
import Logo from "./components/Logo";
import office365AuthService from "./services/office365AuthService";

const StudentLogin = () => {
  const [schoolId, setSchoolId] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogin = async () => {
    if (!schoolId || !dob) {
      setError("Please enter both School ID and Date of Birth");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(buildApiUrl("/synced-students"));
      const students = res.data.students || res.data;
      const matched = students.find(
        (s) =>
          s.school_id === schoolId &&
          s.dob?.slice(0, 10) === dob // normalize both to yyyy-mm-dd
      );

      if (matched) {
        localStorage.setItem("schoolId", matched.school_id);
        login("student", "dummy-token");
      } else {
        setError("Invalid School ID or Date of Birth");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOffice365Login = async () => {
    setError("");
    setLoading(true);

    try {
      // Store flag to indicate student login was initiated
      localStorage.setItem("office365LoginType", "student");
      // Use redirect instead of popup to avoid popup blockers
      await office365AuthService.loginWithRedirect();
      // After redirect, the user will be redirected back and index.js will handle the response
    } catch (err) {
      setError("Office 365 login failed. Please try again.");
      console.error("Office 365 login error:", err);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #00695c 0%, #004d40 100%)', // Teal/Green gradient
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <CssBaseline />
      <Container maxWidth="xs">
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)'
            }
          }}
        >
          <Logo size={50} fontSize="1.5rem" />

          <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#004d40' }}>
            Student Portal
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
            Enter your details to access learning resources
          </Typography>

          <Box component="form" noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="School ID"
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              disabled={loading}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SchoolIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#00695c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00695c',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00695c',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Date of Birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#00695c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00695c',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00695c',
                }
              }}
            />

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 2,
                  textAlign: 'center',
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                  padding: 1,
                  borderRadius: 1
                }}
              >
                {error}
              </Typography>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              startIcon={!loading && <LoginIcon />}
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: '#00695c',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0, 105, 92, 0.3)',
                '&:hover': {
                  backgroundColor: '#004d40',
                  boxShadow: '0 6px 16px rgba(0, 105, 92, 0.4)',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="textSecondary">
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleOffice365Login}
              disabled={loading}
              startIcon={<MicrosoftIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#0078d4',
                color: '#0078d4',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#005a9e',
                  backgroundColor: 'rgba(0, 120, 212, 0.04)',
                },
              }}
            >
              Sign in with Office 365
            </Button>
          </Box>
        </Paper>
        <Typography variant="body2" color="white" align="center" sx={{ mt: 4, opacity: 0.8 }}>
          © {new Date().getFullYear()} School Management System
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link to="/privacy-policy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none', fontSize: '0.875rem' }}>
            Privacy Policy
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default StudentLogin;
